import { useState, useEffect, useRef } from "react";
import { cn } from "@utils";

const PRESET_COLORS = [
  { label: "Black", value: "#000000" },
  { label: "Dark Gray", value: "#374151" },
  { label: "Gray", value: "#6b7280" },
  { label: "Red", value: "#ff0200" },
  { label: "Orange", value: "#ea580c" },
  { label: "Amber", value: "#d97706" },
  { label: "Green", value: "#16a34a" },
  { label: "Teal", value: "#0d9488" },
  { label: "Blue", value: "#2563eb" },
  { label: "Indigo", value: "#4f46e5" },
  { label: "Purple", value: "#9333ea" },
  { label: "Pink", value: "#db2777" },
];

function FormatButton({ children, active, title, onMouseDown, disabled }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={onMouseDown}
      disabled={disabled}
      className={cn(
        "w-7 h-7 flex items-center justify-center rounded text-sm transition-colors select-none",
        disabled
          ? "opacity-40 cursor-not-allowed"
          : active
          ? "bg-zinc-200 text-zinc-900"
          : "text-zinc-600 hover:bg-zinc-100",
      )}
    >
      {children}
    </button>
  );
}

export default function EmailFormattingToolbar({ editorRefs = [] }) {
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [activeColor, setActiveColor] = useState("#000000");
  const [isDisabled, setIsDisabled] = useState(false);
  const colorButtonRef = useRef(null);
  const lastFocusedEl = useRef(null);
  const toolbarRef = useRef(null);

  // Track whichever managed editor was most recently focused, and disable
  // the toolbar when focus moves to a non-formattable field (subject, invoice box).
  useEffect(() => {
    const handleFocusIn = (e) => {
      const inEditor = editorRefs.some((r) => r?.current?.contains(e.target));
      if (inEditor) {
        lastFocusedEl.current = e.target;
        setIsDisabled(false);
      } else if (!toolbarRef.current?.contains(e.target)) {
        setIsDisabled(true);
      }
    };
    document.addEventListener("focusin", handleFocusIn);
    return () => document.removeEventListener("focusin", handleFocusIn);
  }, [editorRefs]);

  useEffect(() => {
    const update = () => {
      const sel = window.getSelection();
      const inEditor = editorRefs.some((r) =>
        r?.current?.contains(sel?.anchorNode),
      );
      if (!inEditor) return;
      try {
        setActiveFormats({
          bold: document.queryCommandState("bold"),
          italic: document.queryCommandState("italic"),
          underline: document.queryCommandState("underline"),
        });
      } catch {
        // queryCommandState may throw in detached frames
      }
    };
    document.addEventListener("selectionchange", update);
    return () => document.removeEventListener("selectionchange", update);
  }, [editorRefs]);

  useEffect(() => {
    if (!colorPickerOpen) return;
    const handleOutside = (e) => {
      if (!colorButtonRef.current?.contains(e.target)) {
        setColorPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [colorPickerOpen]);

  const expandIfZws = () => {
    const sel = window.getSelection();
    if (!sel?.isCollapsed || !sel.rangeCount) return;
    const anchor = sel.anchorNode;
    if (anchor?.nodeType === Node.TEXT_NODE && anchor.textContent === "\u200B") {
      const r = document.createRange();
      r.selectNodeContents(anchor);
      sel.removeAllRanges();
      sel.addRange(r);
      return;
    }
    if (anchor?.nodeType === Node.ELEMENT_NODE) {
      const range = sel.getRangeAt(0);
      const zws = document.createTextNode("\u200B");
      range.insertNode(zws);
      const r = document.createRange();
      r.selectNodeContents(zws);
      sel.removeAllRanges();
      sel.addRange(r);
    }
  };

  const execFormat = (command) => {
    const sel = window.getSelection();
    const savedRange =
      sel?.rangeCount > 0 &&
      editorRefs.some((r) => r?.current?.contains(sel.anchorNode))
        ? sel.getRangeAt(0).cloneRange()
        : null;
    lastFocusedEl.current?.focus();
    if (savedRange) {
      sel.removeAllRanges();
      sel.addRange(savedRange);
    }
    expandIfZws();
    document.execCommand(command, false);
    try {
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    } catch {}
  };

  const applyColor = (color) => {
    lastFocusedEl.current?.focus();
    document.execCommand("foreColor", false, color);
    // execCommand fires the input event synchronously (serializing wrong HTML)
    // before we can fix anything. Structurally swap <u><font color>…</font></u>
    // → <font color><u>…</u></font> so the underline inherits the right colour,
    // then re-fire input so syncValue serializes the corrected DOM.
    let needsSync = false;
    editorRefs.forEach((ref) => {
      ref?.current?.querySelectorAll("u > font[color]").forEach((font) => {
        const u = font.parentNode;
        if (u.childNodes.length !== 1) return;
        const parent = u.parentNode;
        while (font.firstChild) u.appendChild(font.firstChild);
        u.removeChild(font);
        parent.insertBefore(font, u);
        font.appendChild(u);
        needsSync = true;
      });
    });
    if (needsSync) {
      lastFocusedEl.current?.dispatchEvent(new InputEvent("input", { bubbles: true }));
    }
    setActiveColor(color);
    setColorPickerOpen(false);
  };

  return (
    <div ref={toolbarRef} className="flex items-center gap-0.5">
      <FormatButton
        title="Bold (Ctrl+B)"
        active={activeFormats.bold}
        disabled={isDisabled}
        onMouseDown={(e) => {
          e.preventDefault();
          execFormat("bold");
        }}
      >
        <span className="font-bold text-xs">B</span>
      </FormatButton>

      <FormatButton
        title="Italic (Ctrl+I)"
        active={activeFormats.italic}
        disabled={isDisabled}
        onMouseDown={(e) => {
          e.preventDefault();
          execFormat("italic");
        }}
      >
        <span className="italic font-serif text-xs">I</span>
      </FormatButton>

      <FormatButton
        title="Underline (Ctrl+U)"
        active={activeFormats.underline}
        disabled={isDisabled}
        onMouseDown={(e) => {
          e.preventDefault();
          execFormat("underline");
        }}
      >
        <span className="underline text-xs">U</span>
      </FormatButton>

      <div className="w-px h-4 bg-zinc-200 mx-1 shrink-0" />

      <div className="relative" ref={colorButtonRef}>
        <button
          type="button"
          title="Text color"
          disabled={isDisabled}
          onMouseDown={(e) => {
            e.preventDefault();
            if (!isDisabled) setColorPickerOpen((v) => !v);
          }}
          className={cn(
            "w-7 h-7 flex flex-col items-center justify-center rounded transition-colors select-none gap-0.5",
            isDisabled
              ? "opacity-40 cursor-not-allowed"
              : "text-zinc-600 hover:bg-zinc-100",
          )}
        >
          <span className="font-bold text-xs leading-none">A</span>
          <span
            className="w-3.5 h-[3px] rounded-sm"
            style={{ backgroundColor: activeColor }}
          />
        </button>

        {colorPickerOpen && (
          <div className="absolute top-full left-0 mt-1 p-3 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 grid grid-cols-4 gap-3 w-44">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onMouseDown={(e) => {
                  e.preventDefault();
                  applyColor(c.value);
                }}
                className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                style={{
                  backgroundColor: c.value,
                  borderColor:
                    activeColor === c.value ? "#6366f1" : "transparent",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
